import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useExams } from "@/hooks/useExam";
import { examsApi } from "@/api/exams";
import { format } from "date-fns";

export default function AdminExams() {
  const [page, _setPage] = useState(1);
  const queryClient = useQueryClient();
  const { data, isLoading } = useExams(page);

  const deleteMutation = useMutation({
    mutationFn: (id: string) => examsApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["exams"] });
    },
  });

  const toggleActiveMutation = useMutation({
    mutationFn: ({ id, is_active }: { id: string; is_active: boolean }) =>
      examsApi.update(id, { is_active }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["exams"] });
    },
  });

  const handleDelete = (id: string, title: string) => {
    if (window.confirm(`Delete exam "${title}"? This cannot be undone.`)) {
      deleteMutation.mutate(id);
    }
  };

  return (
    <div className="page-enter">
      <div className="rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center py-20">
            <svg className="animate-spin h-6 w-6 text-primary-600" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
            </svg>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead>
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Title
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Questions
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Time Limit
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Pass Mark
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Created
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {data?.map((exam) => (
                    <tr key={exam.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 text-sm font-medium text-gray-900">
                        {exam.title}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500">
                        {exam.total_questions}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500">
                        {exam.time_limit_minutes} min
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500">
                        {exam.pass_mark}/{exam.total_questions}
                      </td>
                      <td className="px-6 py-4">
                        <button
                          onClick={() =>
                            toggleActiveMutation.mutate({
                              id: exam.id,
                              is_active: !exam.is_active,
                            })
                          }
                          className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-bold cursor-pointer ${
                            exam.is_active
                              ? "bg-success-100 text-success-800 hover:bg-success-200"
                              : "bg-gray-100 text-gray-800 hover:bg-gray-200"
                          }`}
                        >
                          {exam.is_active ? "Active" : "Inactive"}
                        </button>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500">
                        {format(new Date(exam.created_at), "dd MMM yyyy")}
                      </td>
                      <td className="px-6 py-4 text-right space-x-3">
                        <button
                          onClick={() => handleDelete(exam.id, exam.title)}
                          className="text-sm font-medium text-danger-600 hover:text-danger-500"
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {data && data.length === 0 && (
              <div className="py-12 text-center text-sm text-gray-500">
                No exams found.
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
